/**
 * Relic Bunker Scene (Premium Merchandise)
 *
 * The inner sanctum, accessible only through the Go Deeper pathway.
 * Same carousel mechanic as Drip, holding rare and premium relics.
 */

import type { Scene } from '@/types/scene';
import type { DripItem } from './drip';
import { fetchProducts, type Product } from '@/lib/supabase';

/* ── Data ────────────────────────────────────────────────────── */

export const items: DripItem[] = [
  {
    id: 'rb-sweater-05',
    name: 'NOCTURNE BEANIE',
    incantation: 'Worn by those who walk between dusk and dawn. \n\nThe thread remembers.',
    image: '/images/relics/Sweater-05.png',
    href: '#',
  },
  {
    id: 'rb-poster-01',
    name: 'BLOOD IN THE CIRCUITS',
    incantation: 'Crimson light stitched into wool. It finds you when the moon is full.',
    image: '/images/relics/poster-01.jpg',
    href: '#',
  },
];

/**
 * Load relic-bunker items from Supabase, falling back to the hardcoded array.
 */
export async function loadBunkerItems(): Promise<Product[]> {
  const remote = await fetchProducts('relic-bunker');
  return remote ?? items;
}

/* ── Scene definition ────────────────────────────────────────── */

export const relicBunker: Scene = {
  id: 'relic-bunker',
  name: 'The Relic Bunker',
  atmosphere: {
    background: 'linear-gradient(180deg, #080510 0%, #0f0a18 50%, #0a0510 100%)',
    fogIntensity: 0.35,
    noiseHue: 280,
    noiseBrightness: 0.7,
    noiseOpacity: 0.55,
    noiseFilter: 'ethereal',
  },
  interactions: [
    {
      id: 'back-home',
      target: '[data-interaction="back-home"]',
      action: { type: 'navigate', sceneId: 'home' },
    },
    {
      id: 'nav-music',
      target: '[data-interaction="nav-music"]',
      action: { type: 'navigate', sceneId: 'music' },
    },
    {
      id: 'nav-drip',
      target: '[data-interaction="nav-drip"]',
      action: { type: 'navigate', sceneId: 'drip' },
    },
    {
      id: 'nav-more',
      target: '[data-interaction="nav-more"]',
      action: { type: 'navigate', sceneId: 'more' },
    },
  ],
  transitions: [
    { to: 'home', effect: 'fade', duration: 800 },
    { to: 'music', effect: 'fade', duration: 800 },
    { to: 'drip', effect: 'fade', duration: 800 },
    { to: 'more', effect: 'fade', duration: 800 },
  ],
  render: () => {
    const first = items.at(0);
    const last = items.at(-1);
    const second = items.at(1);
    if (!first || !last || !second) return '';
    return `
    <div class="pathway-content drip-scene bunker-scene">
      <div class="drip-showcase">
        <div class="drip-ghost drip-ghost-prev" role="button" tabindex="0" aria-label="Previous item">
          <img src="${last.image}" alt="${last.name}" />
        </div>
        <div class="drip-focus">
          <a class="drip-relic-link" href="${first.href}" target="_blank" rel="noopener noreferrer">
            <div class="drip-relic-active">
              <img src="${first.image}" alt="${first.name}" />
            </div>
          </a>
          <div class="drip-incantation">
            <span class="drip-relic-name">${first.name}</span>
            <p class="drip-incantation-text" style="opacity:0">${first.incantation
              .split('\n\n')
              .map((p) => `<span class="rc-para">${p}</span>`)
              .join('')}</p>
          </div>
        </div>
        <div class="drip-ghost drip-ghost-next" role="button" tabindex="0" aria-label="Next item">
          <img src="${second.image}" alt="${second.name}" />
        </div>
        <div class="drip-mask drip-mask-left"></div>
        <div class="drip-mask drip-mask-right"></div>
        <button class="drip-nav drip-nav-prev" aria-label="Previous">\u2039</button>
        <button class="drip-nav drip-nav-next" aria-label="Next">\u203A</button>
      </div>
    </div>
  `;
  },
};
